import { useEffect, useState } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { api } from "../api";
import type { Bootstrap } from "../client";
import { queryKeys } from "../queryKeys";

/** SPEC-APP.md §2.4: past this, Phase 6 shows the waking-up screen. */
const SLOW_AFTER_MS = 4000;

/** The query result, plus `isSlow`. */
export type UseBootstrapResult = UseQueryResult<Bootstrap, Error> & {
  /** True once the bootstrap request has been pending for more than 4 s. */
  isSlow: boolean;
};

export function useBootstrap(): UseBootstrapResult {
  const query = useQuery({
    queryKey: queryKeys.bootstrap(),
    queryFn: () => api.call("getBootstrap"),
  });

  const [isSlow, setIsSlow] = useState(false);
  const pending = query.isPending || query.isFetching;

  useEffect(() => {
    if (!pending) {
      setIsSlow(false);
      return;
    }
    const timer = setTimeout(() => setIsSlow(true), SLOW_AFTER_MS);
    return () => clearTimeout(timer);
  }, [pending]);

  return { ...query, isSlow } as UseBootstrapResult;
}

/**
 * The tenant key every other hook builds its key with. Throws in dev when called before
 * bootstrap resolved — a key without the tenant key is a bug, not a fallback.
 */
export function useTenantKey(): string {
  const { data } = useBootstrap();
  const key = data?.tenant.key;
  if (!key) {
    if (import.meta.env.DEV) {
      throw new Error("useTenantKey() called before bootstrap resolved.");
    }
    return "";
  }
  return key;
}
